import cn from 'clsx';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import { useI18n } from '#src/hooks/i18n';
import { type YearsMode } from '#src/types';
import { OptionTag } from '../ui/option-tag';

function getPossibleRangeYears(
  years: number[],
  yearsOptions: number[]
): number[] {
  const y: number[] = [];
  if (years[0] > years[1] || years.length < 2) {
    y.push(years[0]);

    if (yearsOptions.includes(years[0] + 1)) {
      y.push(years[0] + 1);
    } else if (yearsOptions.includes(years[0] - 1)) {
      y.unshift(years[0] - 1);
    }
  } else {
    y.push(years[0]);
    y.push(years[1]);
  }
  return y;
}
export function YearsDropdown({
  yearsMode,
  selectedYears,
  yearsOptions,
  onChange,
  className,
  modes,
  multipleYearsLimit,
}: {
  yearsMode: YearsMode;
  selectedYears: number[];
  yearsOptions: number[];
  className?: string;
  modes: YearsMode[];
  onChange: (yearsMode: YearsMode, selectedYears: number[]) => void;
  multipleYearsLimit: number;
}) {
  const { t } = useI18n();
  const [state, setState] = useState({
    yearsMode,
    selectedYears,
  });
  const [show, setShow] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback(
    (yearsMode: YearsMode, years: number[]) => {
      let selectedYears = years;

      if (yearsMode === 'range') {
        selectedYears = getPossibleRangeYears(years, yearsOptions);
      }

      if (yearsMode === 'multiple') {
        selectedYears = years.sort((a, b) => a - b);
      }

      setState({ yearsMode, selectedYears });
    },
    [yearsOptions]
  );

  const handleClear = () => {
    const lastYear = yearsOptions[yearsOptions.length - 1];
    const newState = {
      yearsMode: 'single' as YearsMode,
      selectedYears: lastYear ? [Number(lastYear)] : [],
    };
    setState(newState);
    onChange(newState.yearsMode, newState.selectedYears);
    setShow(false);
  };
  const handleCancel = () => {
    setState({
      yearsMode,
      selectedYears,
    });
    setShow(false);
  };

  const handleApply = () => {
    const selectedYears = state.selectedYears.filter((year) => year != null);
    if (selectedYears.length === 0) {
      return;
    }
    onChange(state.yearsMode, selectedYears);
    setShow(false);
  };

  const missingModes = ['single', 'multiple', 'range'].filter(
    (mode) => !modes.includes(mode as YearsMode)
  );

  const showMissingWarning = missingModes.length > 0;
  const showMultipleSelectionLimitWarning =
    state.yearsMode === 'multiple' &&
    state.selectedYears.length >= multipleYearsLimit;

  const possibleRangeYears = getPossibleRangeYears(
    state.selectedYears,
    yearsOptions
  );

  useEffect(() => {
    handleChange(yearsMode, selectedYears);
  }, [yearsMode, selectedYears, handleChange]);

  const yearOptionsFiltered = yearsOptions.filter(
    (year) => !state.selectedYears.includes(year)
  );

  return (
    <Dropdown
      className={cn('years-dropdown', className)}
      show={show}
      ref={dropdownRef}
      onToggle={(nextShow, event) => {
        if (!nextShow) {
          if (
            event &&
            event.type === 'click' &&
            dropdownRef.current &&
            dropdownRef.current.contains(event.target as Node)
          ) {
            // Click inside dropdown, ignore close
            return;
          }
          setShow(false);
        } else {
          setShow(true);
        }
      }}
    >
      <Dropdown.Toggle
        variant='light'
        className='text-start w-100 d-flex justify-content-between align-items-center dropdown-toggle-border years-dropdown-toggle'
        onClick={() => setShow(!show)}
      >
        <p
          className='years-dropdown-toggle-text max-width-100 overflow-hidden'
          style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          <span className='text-secondary mr-2'>
            {t('filters.years-dropdown.in-label')}
          </span>
          {yearsMode === 'single' && (
            <span className='fw-bold'>{selectedYears[0]?.toString()}</span>
          )}
          {yearsMode === 'range' && (
            <span className='fw-bold'>
              {selectedYears[0]?.toString()} – {selectedYears[1]?.toString()}
            </span>
          )}
          {yearsMode === 'multiple' && (
            <span className='fw-bold'>
              {selectedYears.map((year) => year.toString()).join(', ')}
            </span>
          )}
        </p>
      </Dropdown.Toggle>
      <Dropdown.Menu className='years-dropdown-menu'>
        <div className='px-2 bg-white rounded-bottom'>
          <Form className='d-flex flex-column'>
            <div className='d-flex flex-column w-100 mb-3'>
              <Form.Check
                type='radio'
                id='single'
                name='yearsMode'
                disabled={!modes.includes('single')}
                label={t('filters.years-dropdown.mode.single')}
                value='single'
                checked={state.yearsMode === 'single'}
                onChange={(ev) =>
                  handleChange(
                    ev.target.value as YearsMode,
                    state.selectedYears.slice(0, 1)
                  )
                }
                className='mb-2 text-dark small'
              />
              <Dropdown
                className='flex-grow-1'
                onSelect={(value, event) => {
                  event.preventDefault();
                  if (!value) return;
                  handleChange(state.yearsMode, [Number(value)]);
                }}
              >
                <Dropdown.Toggle
                  variant='light'
                  disabled={state.yearsMode !== 'single'}
                  className='bg-white w-100 d-flex justify-content-between align-items-center dropdown-toggle-border'
                >
                  <span className='font-weight-normal small'>
                    {state.selectedYears[0] ?? selectedYears[0]}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className='years-dropdown-menu-inner'>
                  {yearOptionsFiltered.map((year) => (
                    <Dropdown.Item
                      as='button'
                      key={year}
                      eventKey={year}
                      className='small'
                    >
                      {year.toString()}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className='d-flex flex-column w-100'>
              <div className='d-flex flex-column w-100'>
                {showMissingWarning && (
                  <div className='text-muted small'>
                    {t(
                      `filters.years-dropdown.missing-${missingModes.join('-')}`
                    )}
                  </div>
                )}
              </div>
              {modes.includes('range') && (
                <div className='d-flex flex-column w-100'>
                  <Form.Check
                    type='radio'
                    id='range'
                    name='yearsMode'
                    label={t('filters.years-dropdown.mode.range')}
                    value='range'
                    disabled={!modes.includes('range')}
                    checked={state.yearsMode === 'range'}
                    onChange={(ev) =>
                      handleChange(ev.target.value as YearsMode, [
                        possibleRangeYears[0],
                        possibleRangeYears[1],
                      ])
                    }
                    className='mb-2 text-dark font-size-14 small'
                  />
                  <div className='d-flex flex-row align-items-center mb-3'>
                    <Dropdown
                      className='flex-grow-1 mr-2'
                      onSelect={(value, event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!value) return;
                        handleChange(
                          state.yearsMode,
                          [Number(value), state.selectedYears[1]].filter(
                            (y) => y != null
                          )
                        );
                      }}
                    >
                      <Dropdown.Toggle
                        variant='light'
                        disabled={state.yearsMode !== 'range'}
                        className='bg-white w-100 d-flex justify-content-between align-items-center dropdown-toggle-border'
                      >
                        <span className='font-weight-normal small'>
                          {possibleRangeYears[0] ?? selectedYears[0]}
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className='years-dropdown-menu-inner range'>
                        {yearOptionsFiltered.map((year) => (
                          <Dropdown.Item
                            as='button'
                            key={year}
                            eventKey={year}
                            active={year === state.selectedYears[0]}
                            className='small'
                          >
                            {year.toString()}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown
                      className='flex-grow-1'
                      onSelect={(value, event) => {
                        event.preventDefault();
                        if (!value) return;
                        handleChange(state.yearsMode, [
                          state.selectedYears[0],
                          Number(value),
                        ]);
                      }}
                    >
                      <Dropdown.Toggle
                        variant='light'
                        disabled={
                          state.yearsMode !== 'range' ||
                          yearOptionsFiltered.filter(
                            (year) => year > state.selectedYears[0]
                          ).length < 1
                        }
                        className='bg-white w-100 d-flex justify-content-between align-items-center dropdown-toggle-border'
                      >
                        <span className='font-weight-normal small'>
                          {possibleRangeYears[1] ?? selectedYears[1]}
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu className='years-dropdown-menu-inner range'>
                        {yearOptionsFiltered
                          .filter((year) => year > state.selectedYears[0])
                          .map((year) => (
                            <Dropdown.Item
                              as='button'
                              key={year}
                              eventKey={year}
                              active={year === state.selectedYears[1]}
                              className='small'
                            >
                              {year.toString()}
                            </Dropdown.Item>
                          ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              )}
              {modes.includes('multiple') && (
                <div className='d-flex flex-column w-100'>
                  <Form.Check
                    type='radio'
                    id='multiple'
                    name='yearsMode'
                    disabled={!modes.includes('multiple')}
                    label={t('filters.years-dropdown.mode.multiple')}
                    value='multiple'
                    checked={state.yearsMode === 'multiple'}
                    onChange={(ev) =>
                      handleChange(ev.target.value as YearsMode, [])
                    }
                    className='mb-2 text-dark small'
                  />
                  <Dropdown
                    className='flex-grow-1'
                    onSelect={(value, event) => {
                      event.preventDefault();
                      const values = state.selectedYears.includes(Number(value))
                        ? state.selectedYears.filter((y) => y !== Number(value))
                        : [...state.selectedYears, Number(value)];
                      handleChange(state.yearsMode, values);
                    }}
                  >
                    <MultipleYearsToggle
                      state={state}
                      handleChange={handleChange}
                      multipleYearsLimit={multipleYearsLimit}
                    />
                    <Dropdown.Menu className='years-dropdown-menu-inner'>
                      {yearOptionsFiltered.map((year) => (
                        <Dropdown.Item
                          key={year}
                          as='button'
                          eventKey={year.toString()}
                          className='d-flex align-items-center'
                        >
                          {year.toString()}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  {showMultipleSelectionLimitWarning && (
                    <div className='text-muted small'>
                      {t(
                        'filters.years-dropdown.multiple-selection-limit-reached'
                      ).replace('{limit}', multipleYearsLimit.toString())}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Form>
          <div className='years-dropdown-actions d-flex mt-3 w-100 overflow-hidden'>
            <Button
              variant='link'
              className='text-secondary flex-grow-1 btn-xs mr-2 text-nowrap py-2 px-0 font-weight-normal'
              onClick={handleClear}
            >
              {t('filters.common.clear')}
            </Button>
            <Button
              variant='light'
              className='border-secondary flex-grow-1 btn-xs mr-2 text-nowrap py-2'
              onClick={handleCancel}
            >
              {t('filters.common.cancel')}
            </Button>
            <Button
              variant='secondary'
              disabled={
                state.selectedYears.filter((year) => year != null).length === 0
              }
              className='border-secondary flex-grow-1 btn-xs text-nowrap py-2'
              onClick={handleApply}
            >
              {t('filters.common.apply')}
            </Button>
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function MultipleYearsToggle({
  state,
  handleChange,
  multipleYearsLimit,
}: {
  state: {
    yearsMode: YearsMode;
    selectedYears: number[];
  };
  handleChange: (yearsMode: YearsMode, selectedYears: number[]) => void;
  multipleYearsLimit: number;
}) {
  const { t } = useI18n();

  const content = (
    <>
      {(state.yearsMode !== 'multiple' || state.selectedYears.length === 0) && (
        <span className='font-weight-normal small'>
          {t('filters.years-dropdown.select-years-placeholder')}
        </span>
      )}
      {state.yearsMode === 'multiple' && state.selectedYears.length > 0 && (
        <div className='d-flex flex-row flex-wrap align-items-center'>
          {state.selectedYears.map((item) => (
            <OptionTag
              key={item}
              label={item.toString()}
              onClick={() => {
                handleChange(
                  state.yearsMode,
                  state.selectedYears.filter((y) => y !== item)
                );
              }}
            />
          ))}
        </div>
      )}
    </>
  );

  if (state.selectedYears.length >= multipleYearsLimit) {
    return (
      <div className='bg-white w-100 d-flex justify-content-between align-items-center py-1 px-2 dropdown-toggle-border'>
        {content}
      </div>
    );
  }

  return (
    <Dropdown.Toggle
      variant='light'
      disabled={state.yearsMode !== 'multiple'}
      className='bg-white w-100 d-flex justify-content-between align-items-center py-1 px-2 dropdown-toggle-border'
    >
      {content}
    </Dropdown.Toggle>
  );
}
